import { useRef, useCallback, useEffect } from 'react';

interface QuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  onInputFocusChange?: (focused: boolean) => void;
}

export function QuantityInput({ value, onChange, min = 0, max = 999, onInputFocusChange }: QuantityInputProps) {
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const tickCountRef = useRef(0);
  const valueRef = useRef(value);

  valueRef.current = value;

  const stop = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = undefined; }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = undefined; }
    tickCountRef.current = 0;
  }, []);

  useEffect(() => stop, [stop]);

  const startHold = useCallback((direction: 1 | -1) => {
    stop(); // clear any previous hold

    const apply = () => {
      tickCountRef.current++;
      let next: number;
      if (tickCountRef.current > 8) {
        // Snap to next multiple of 5 in the given direction
        if (direction === 1) {
          next = Math.ceil((valueRef.current + 1) / 5) * 5;
        } else {
          next = Math.floor((valueRef.current - 1) / 5) * 5;
        }
      } else {
        next = valueRef.current + direction;
      }
      onChange(Math.max(min, Math.min(next, max)));
    };

    apply();

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(apply, 120);
    }, 400);
  }, [min, max, onChange, stop]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') { onChange(0); return; }
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed)) {
      onChange(Math.max(min, Math.min(parsed, max)));
    }
  }, [onChange, min, max]);

  const btnBase = 'flex items-center justify-center select-none font-bold transition-colors touch-none';
  const btnDesktop = 'h-8 w-8 sm:h-8 sm:w-8 text-lg border border-green-600 bg-gray-50 text-green-600 hover:bg-gray-100 active:bg-gray-200';
  const btnMobile = 'h-10 w-10 text-xl border border-green-600 bg-gray-50 text-green-600 active:bg-gray-100';

  return (
    <div className="flex items-center">
      {/* Desktop − */}
      <button
        type="button"
        className={`${btnBase} ${btnDesktop} rounded-l-md hidden sm:flex`}
        onPointerDown={() => startHold(-1)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        aria-label="Quitar"
      >−</button>
      {/* Mobile − */}
      <button
        type="button"
        className={`${btnBase} ${btnMobile} rounded-l-lg sm:hidden`}
        onPointerDown={() => startHold(-1)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        aria-label="Quitar"
      >−</button>

      {/* Desktop input */}
      <input
        type="text"
        inputMode="numeric"
        className="hidden sm:block h-8 w-12 border-y border-green-600 bg-white text-center text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-green-500"
        value={value === 0 ? '' : value}
        onChange={handleInputChange}
        onFocus={() => onInputFocusChange?.(true)}
        onBlur={() => onInputFocusChange?.(false)}
        onKeyDown={(e) => { if (e.key === 'Escape') (e.target as HTMLInputElement).blur(); }}
        placeholder="0"
      />
      {/* Mobile input */}
      <input
        type="text"
        inputMode="numeric"
        className="sm:hidden h-10 w-14 border-y border-green-600 bg-white text-center text-base font-medium text-gray-800 focus:outline-none focus:ring-1 focus:ring-green-500"
        value={value === 0 ? '' : value}
        onChange={handleInputChange}
        placeholder="0"
      />

      {/* Desktop + */}
      <button
        type="button"
        className={`${btnBase} ${btnDesktop} rounded-r-md hidden sm:flex`}
        onPointerDown={() => startHold(1)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        aria-label="Agregar"
      >+</button>
      {/* Mobile + */}
      <button
        type="button"
        className={`${btnBase} ${btnMobile} rounded-r-lg sm:hidden`}
        onPointerDown={() => startHold(1)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        aria-label="Agregar"
      >+</button>
    </div>
  );
}
